import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { Decimal } from 'decimal.js'
import type { TaxRatesResponse } from '@/api/settings'
import type { TaxRateVersion } from '@/domain/tax'
import { BASIS_POINTS_FULL, isQuantity, minorFromMajorString } from '@/domain/money'
import { apiFetch } from '~/api/client'

export type { TaxRateVersion, TaxRatesResponse }

const TAX_KEY = ['settings', 'tax'] as const

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const PERCENT_PER_FULL = BASIS_POINTS_FULL / 100

export class TaxRateDraftError extends Error {}

export function useTaxRates(): UseQueryResult<TaxRatesResponse> {
  return useQuery({
    queryKey: TAX_KEY,
    queryFn: () => apiFetch<TaxRatesResponse>('/settings/tax'),
  })
}

/**
 * The whole list every time. A rate is not a preference that applies from now
 * on — the version dated 2023 is what reprices 2023 — so an edit is a statement
 * about the history as well as about today, and the route takes it as one.
 */
export function useSaveTaxRates(): UseMutationResult<
  { key: string; versions: TaxRateVersion[] },
  Error,
  readonly TaxRateVersion[]
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (versions: readonly TaxRateVersion[]) =>
      apiFetch<{ key: string; versions: TaxRateVersion[] }>('/settings/tax', {
        method: 'PUT',
        body: JSON.stringify({ versions }),
      }),
    // Every figure that subtracts tax is every figure on every page.
    onSuccess: () => client.invalidateQueries(),
  })
}

/** A version as the boxes that restate it: percentages, and an amount as typed. */
export interface TaxRateDraft {
  effectiveFrom: string
  corporateGainsPercent: string
  dividendLowPercent: string
  dividendHighPercent: string
  dividendThresholdMajor: string
}

export function draftFromVersion(version: TaxRateVersion): TaxRateDraft {
  return {
    effectiveFrom: version.effectiveFrom,
    corporateGainsPercent: percentOf(version.corporateGainsRateBp),
    dividendLowPercent: percentOf(version.dividendLowRateBp),
    dividendHighPercent: percentOf(version.dividendHighRateBp),
    dividendThresholdMajor: majorOf(version.dividendThresholdMinor),
  }
}

/**
 * A new version carries the numbers of the one before it. Three of the four
 * rarely move between years, and a blank row invites the fourth to be typed
 * alone and the rest left at nothing — which is not a version with a gap in it
 * but a jurisdiction that charges no dividend tax at all.
 */
export function nextDraftAfter(previous: TaxRateDraft | undefined): TaxRateDraft {
  if (previous === undefined) {
    return {
      effectiveFrom: '',
      corporateGainsPercent: '',
      dividendLowPercent: '',
      dividendHighPercent: '',
      dividendThresholdMajor: '',
    }
  }
  return { ...previous, effectiveFrom: '' }
}

/** Why this version cannot be saved yet, in the words of whoever would refuse it. */
export function refuseTaxRateDraft(
  draft: TaxRateDraft,
  otherDates: readonly string[] = [],
): string | undefined {
  if (!ISO_DATE.test(draft.effectiveFrom)) return 'a date in the form YYYY-MM-DD is required'
  if (otherDates.includes(draft.effectiveFrom)) {
    return `two versions start on ${draft.effectiveFrom}, so the rates on that date are ambiguous`
  }

  const rates: readonly (readonly [string, string])[] = [
    ['corporate gains', draft.corporateGainsPercent],
    ['the lower dividend band', draft.dividendLowPercent],
    ['the upper dividend band', draft.dividendHighPercent],
  ]
  for (const [what, percent] of rates) {
    if (basisPointsOf(percent) === null) return `${what} is a percentage between 0 and 100`
  }

  if (minorOf(draft.dividendThresholdMajor) === null) {
    return 'the threshold is an amount of zero or more'
  }

  return undefined
}

export function toTaxRateVersion(draft: TaxRateDraft): TaxRateVersion {
  const refusal = refuseTaxRateDraft(draft)
  if (refusal !== undefined) throw new TaxRateDraftError(refusal)

  const corporateGainsRateBp = basisPointsOf(draft.corporateGainsPercent)
  const dividendLowRateBp = basisPointsOf(draft.dividendLowPercent)
  const dividendHighRateBp = basisPointsOf(draft.dividendHighPercent)
  const dividendThresholdMinor = minorOf(draft.dividendThresholdMajor)

  if (
    corporateGainsRateBp === null ||
    dividendLowRateBp === null ||
    dividendHighRateBp === null ||
    dividendThresholdMinor === null
  ) {
    throw new TaxRateDraftError('a rate this form accepted is not a number after all')
  }

  return {
    effectiveFrom: draft.effectiveFrom,
    corporateGainsRateBp,
    dividendLowRateBp,
    dividendHighRateBp,
    dividendThresholdMinor,
  }
}

/**
 * The first refusal among the drafts, each checked against the dates of all the
 * others so that a clash is caught whichever of the two was typed second.
 */
export function refuseTaxRateDrafts(drafts: readonly TaxRateDraft[]): string | undefined {
  return drafts
    .map((draft, index) =>
      refuseTaxRateDraft(
        draft,
        drafts.filter((_, other) => other !== index).map((other) => other.effectiveFrom),
      ),
    )
    .find((refusal) => refusal !== undefined)
}

export function sortDrafts(drafts: readonly TaxRateDraft[]): TaxRateDraft[] {
  return [...drafts].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
}

export function percentOf(bp: number): string {
  return new Decimal(bp).dividedBy(PERCENT_PER_FULL).toFixed()
}

export function basisPointsOf(percent: string): number | null {
  const trimmed = percent.trim()
  if (!isQuantity(trimmed)) return null
  const bp = new Decimal(trimmed).times(PERCENT_PER_FULL).toDecimalPlaces(0).toNumber()
  return bp >= 0 && bp <= BASIS_POINTS_FULL ? bp : null
}

function majorOf(amountMinor: number): string {
  return new Decimal(amountMinor).dividedBy(100).toFixed()
}

function minorOf(major: string): number | null {
  const trimmed = major.trim()
  if (!isQuantity(trimmed)) return null
  try {
    const minor = minorFromMajorString(trimmed)
    return minor < 0 ? null : minor
  } catch {
    return null
  }
}
